import { useState } from 'react';

import DeleteOutlineTwoToneIcon from '@mui/icons-material/DeleteOutlineTwoTone';
import { Button, Tooltip } from '@mui/material';
import {
  GridActionsCellItem,
  GridRenderCellParams,
  GridRowParams
} from '@mui/x-data-grid';
import { UseQueryResult } from '@tanstack/react-query';
import useDisputes from 'api/useDisputes';
import {
  DisputeStatus,
  IDisputeEvidenceResponse,
  OrderDetails
} from 'common/contracts';
import { DATE_TIME_FORMAT } from 'constants/constants';
import { EButtonColor } from 'constants/enums';
import dayjs from 'dayjs';
import { permissionsUtil } from 'utils/permissionsUtil';
import { EToasterStatus, showToast } from 'utils/showToast';

import DataTable from 'components/DataTable/DataTable';
import DialogModal from 'components/Dialog/Dialog';

export default function DisputeEvidenceTable({
  order
}: {
  order: UseQueryResult<OrderDetails, unknown>;
}) {
  const [showEvidenceDialog, setShowEvidenceDialog] = useState<boolean>(false);
  const [selectedEvidenceID, setSelectedEvidenceID] = useState<string | null>(
    null
  );
  const [showSubmitAllDialog, setShowSubmitAllDialog] =
    useState<boolean>(false);

  const isEvidenceSubmissionDisabled =
    order.data?.dispute.status === DisputeStatus.EVIDENCE_SUBMITTED ||
    order.data?.dispute.status === DisputeStatus.UNDER_REVIEW ||
    order.data?.dispute.status === DisputeStatus.WON ||
    order.data?.dispute.status === DisputeStatus.LOST;

  const { deleteDisputeEvidence, getSignedUrlForEvidenceFile, submitEvidence } =
    useDisputes(
      order.data?.dispute?._id,
      order?.data?.offerSnapshot?.publisherId
    );

  const onDeleteEvidence = () => {
    if (selectedEvidenceID) {
      deleteDisputeEvidence.mutate(selectedEvidenceID, {
        onSuccess: () => {
          showToast({
            message: 'Evidence deleted successfully',
            status: EToasterStatus.SUCCESS
          });
          order.refetch();
          setSelectedEvidenceID(null);
          setShowEvidenceDialog(false);
        },
        onError: () => {
          showToast({
            message: 'Error deleting evidence',
            status: EToasterStatus.ERROR
          });
        }
      });
    }
  };

  function extractFileName(path: string | undefined): string | undefined {
    if (!path) return;
    const parts = path.split('/');
    const lastPart = parts[parts.length - 1];
    const withoutExtension = lastPart.replace(/\.[^/.]+$/, '');
    const nameStart = withoutExtension.indexOf('-') + 1;
    return withoutExtension.slice(nameStart);
  }

  const handleDownload = (filePath: string, evidenceId: string) => {
    getSignedUrlForEvidenceFile.mutate(
      {
        filePath,
        evidenceId
      },
      {
        onSuccess: ({ signedUrls }: { signedUrls: string[] }) => {
          window.open(signedUrls[0], '_blank');
        },
        onError: () => {
          showToast({
            message: 'Error downloading evidence',
            status: EToasterStatus.ERROR
          });
        }
      }
    );
  };

  const handleSubmitAll = () => {
    submitEvidence.mutate(undefined, {
      onSuccess: () => {
        showToast({
          message: 'Evidence submitted successfully',
          status: EToasterStatus.SUCCESS
        });
        order.refetch();
        setShowSubmitAllDialog(false);
      }
    });
  };

  return (
    <>
      <DataTable
        className="order__products-table"
        initialSorting={{
          sortModel: [
            {
              field: 'createdAt',
              sort: 'desc'
            }
          ]
        }}
        columns={[
          {
            field: 'name',
            headerName: 'Evidence Name',
            flex: 1,
            renderCell: (
              params: GridRenderCellParams<any, IDisputeEvidenceResponse, any>
            ) => {
              const name = params.row.name;
              return (
                <Tooltip title={name}>
                  <div
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      paddingRight: '10px'
                    }}
                  >
                    {name}
                  </div>
                </Tooltip>
              );
            }
          },
          {
            field: 'description',
            headerName: 'Description',
            flex: 1,
            renderCell: (
              params: GridRenderCellParams<any, IDisputeEvidenceResponse, any>
            ) => {
              const description = params.row.description;
              return (
                <Tooltip title={description}>
                  <div
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      paddingRight: '10px'
                    }}
                  >
                    {description}
                  </div>
                </Tooltip>
              );
            }
          },
          {
            field: 'username',
            headerName: 'User Name',
            flex: 1
          },
          {
            field: 'createdAt',
            headerName: 'Created At',
            flex: 1,
            renderCell: (
              params: GridRenderCellParams<any, IDisputeEvidenceResponse, any>
            ) => {
              return (
                <span>
                  {dayjs(params.row.createdAt).utc().format(DATE_TIME_FORMAT)}
                </span>
              );
            }
          },
          {
            field: 'file',
            headerName: 'File',
            flex: 1,
            renderCell: (
              params: GridRenderCellParams<any, IDisputeEvidenceResponse, any>
            ) => {
              const displayName = extractFileName(params.row?.filePath);
              if (!displayName || !params.row?.filePath) return null;
              return (
                <div
                  onClick={() =>
                    handleDownload(
                      params.row.filePath as string,
                      params.row._id
                    )
                  }
                  style={{
                    color: '#1976d2',
                    textDecoration: 'underline',
                    cursor: 'pointer'
                  }}
                >
                  {displayName}
                </div>
              );
            }
          },
          {
            field: 'actions',
            headerName: 'Actions',
            renderHeader: () => (
              <Button
                variant="contained"
                color="primary"
                onClick={() => setShowSubmitAllDialog(true)}
                disabled={isEvidenceSubmissionDisabled}
              >
                {submitEvidence.isLoading
                  ? 'Submitting...'
                  : isEvidenceSubmissionDisabled
                    ? 'Submitted'
                    : 'Submit All'}
              </Button>
            ),
            headerClassName: 'actions-header',
            flex: 0,
            align: 'right',
            type: 'actions',
            width: 200,
            disableReorder: true,
            getActions: (params: GridRowParams<IDisputeEvidenceResponse>) => [
              <GridActionsCellItem
                className="danger"
                icon={<DeleteOutlineTwoToneIcon className="danger" />}
                label="Delete"
                onClick={() => {
                  setSelectedEvidenceID(params.row._id);
                  setShowEvidenceDialog(true);
                }}
                disabled={!permissionsUtil.canUserEdit()}
                showInMenu
              />
            ]
          }
        ]}
        rows={order.data?.dispute.evidences || []}
        loading={false}
      ></DataTable>
      <DialogModal
        isOpen={showEvidenceDialog}
        headline="Delete Evidence"
        text="Are you sure you want to delete this evidence? This can't be undone."
        buttons={[
          {
            text: 'Cancel',
            color: EButtonColor.SECONDARY,
            variant: 'outlined',
            func: () => {
              setShowEvidenceDialog(false);
            }
          },
          {
            text: 'Delete',
            color: EButtonColor.ERROR,
            variant: 'contained',
            disabled: deleteDisputeEvidence.isLoading,
            func: onDeleteEvidence
          }
        ]}
        closeDialog={() => setShowEvidenceDialog(false)}
      />
      <DialogModal
        isOpen={showSubmitAllDialog}
        headline="Evidence Submission"
        text="Once submitted, no changes or additions can be made."
        buttons={[
          {
            text: 'Cancel',
            color: EButtonColor.SECONDARY,
            variant: 'outlined',
            func: () => {
              setShowSubmitAllDialog(false);
            }
          },
          {
            text: 'Submit',
            color: EButtonColor.PRIMARY,
            variant: 'contained',
            func: () => {
              handleSubmitAll();
            }
          }
        ]}
      />
    </>
  );
}
